// components
import Component from '@glimmer/component';
import {
  HdsApplicationState,
  HdsBadge,
  HdsRichTooltip,
  HdsPaginationNumbered,
  HdsTable,
  HdsTextBody,
} from '@hashicorp/design-system-components/components';
import Flex from 'core/components/flex';
import RoleBadge from '../role-badge.gts';

// helpers
import { get } from '@ember/object';
import { t } from 'ember-intl';
import { eq, notEq, or } from 'ember-truth-helpers';
import { findBy } from '@nullvoxpopuli/ember-composable-helpers';
// @ts-expect-error: fix incoming types
import lowercase from 'ember-cli-string-helpers/helpers/lowercase';
import optionForSourceState from 'core/helpers/option-for-source-state';
import iamFindRoleFromRoles from '../../../helpers/iam-find-role-from-roles.ts';

// modifiers
import { on } from '@ember/modifier';
import boxPadding from 'core/modifiers/box-padding';

// utils
import { HashicorpCloudResourcemanagerPolicyBindingMemberType } from '@clients/cloud-resource-manager';
import { AlignItem, FlexDirection, FlexGap } from 'core/utils/flex';
import { DEFAULT_PAGE_SIZES } from '../../../helpers/iam-paginate-array.ts';
import type { RolesLocation } from '../../../types/roles-location.ts';

export interface MemberRoleAssignmentsTableSignature {
  Args: {
    currentRolesLocation: RolesLocation;
    currentScopeLink: {
      id: string;
      name: string;
      type: string;
    };
    groups: Array<{
      displayName: string;
      resourceId: string;
    }>;
    handleOnClearFilters: () => void;
    handleOnPageChanged: (page: string) => void;
    handleOnRolesPageSizeChanged: (pageSize: string) => void;
    isFiltered: string | number;
    roleAssignments: {
      assignments: Array<{
        link: {
          id: string;
          name: string;
          type: string;
        };
        member: {
          memberId: string;
          memberType: string;
        };
        roleId: string;
      }>;
      currentPage: number;
      currentPageSize: number;
      totalItems: number;
    };
    rolesLocations: RolesLocation[];
  };
  Blocks: Record<string, never>;
  Element: HTMLDivElement;
}

/**
 * Component: MemberRoleAssignmentsTableComponent
 *
 * This component is responsible for rendering a table that displays role assignments for members.
 * It includes functionality for pagination, filtering, and displaying detailed information about
 * roles, descriptions, scopes, and assignment details.
 *
 * Features:
 * - Displays a table of role assignments with columns for role, description, scope, and granted-at details.
 * - Supports dynamic page sizes and pagination using `HdsPaginationNumbered`.
 * - Handles scenarios where no data is available, with appropriate UI states for filtered and unfiltered cases.
 * - Provides tooltips and badges for additional context on roles, groups, and inheritance.
 * - Integrates with translation utilities (`t`) for localized text.
 *
 * Template Structure:
 * - Uses `HdsTable` for rendering the role assignments table.
 * - Each row in the table represents a role assignment, with detailed cells for role, description, scope, and granted-at.
 * - Includes rich tooltips (`HdsRichTooltip`) for displaying contextual information about roles and groups.
 * - Displays inherited roles with a badge and tooltip for clarity.
 * - Provides a fallback UI (`HdsApplicationState`) for cases where no role assignments are available.
 *
 * Arguments:
 * - `@roleAssignments`: An object containing role assignment data, including `assignments`, `currentPage`, `currentPageSize`, and `totalItems`.
 * - `@currentScopeLink`: An object representing the current scope, with properties `type`, `name`, and `id`.
 * - `@rolesLocations`: A list of roles and their associated locations.
 * - `@currentRolesLocation`: The current location of roles.
 * - `@groups`: A list of groups for resolving group-related information.
 * - `@isFiltered`: A boolean indicating whether the data is filtered.
 * - `@handleOnPageChanged`: A callback for handling page changes in pagination.
 * - `@handleOnRolesPageSizeChanged`: A callback for handling page size changes in pagination.
 * - `@handleOnClearFilters`: A callback for clearing applied filters.
 *
 * Computed Properties:
 * - `pageSizes`: Dynamically calculates available page sizes based on the current page size and default sizes.
 *
 * Usage:
 * This component is used to manage and display role assignments in a tabular format, with support for
 * pagination, filtering, and detailed contextual information. It is designed to be integrated into
 * a larger application where role management is required.
 */
export default class MemberRoleAssignmentsTableComponent extends Component<MemberRoleAssignmentsTableSignature> {
  get pageSizes() {
    const { currentPageSize } = this.args.roleAssignments;
    return [...new Set([currentPageSize, ...DEFAULT_PAGE_SIZES])];
  }

  <template>
    {{#let @roleAssignments.assignments as |assignments|}}
      {{#if assignments.length}}
        <Flex @direction={{FlexDirection.Column}} @gap={{FlexGap.Md}}>
          <HdsTable
            @caption={{t
              "manage-access.components.table.member-role-assignments.role-assignments"
            }}
            @valign="top"
            data-test-role-assignments-list-table
            data-test-role-assignments-list-scope-type={{@currentScopeLink.type}}
            data-test-role-assignments-list-scope-name={{@currentScopeLink.name}}
            data-test-role-assignments-list-scope-id={{@currentScopeLink.id}}
          >
            <:head as |H|>
              <H.Tr>
                <H.Th
                  @width="20%"
                  data-test-role-assignments-list-table-header-name="role"
                >
                  {{t
                    "manage-access.components.table.member-role-assignments.role"
                  }}
                </H.Th>
                <H.Th
                  data-test-role-assignments-list-table-header-name="description"
                >
                  {{t
                    "manage-access.components.table.member-role-assignments.description"
                  }}
                </H.Th>
                <H.Th
                  @width="20%"
                  data-test-role-assignments-list-table-header-name="scope"
                >
                  {{t
                    "manage-access.components.table.member-role-assignments.scope"
                  }}
                </H.Th>
                <H.Th
                  @width="20%"
                  data-test-role-assignments-list-table-header-name="granted-at"
                >
                  {{t
                    "manage-access.components.table.member-role-assignments.granted-at"
                  }}
                </H.Th>
              </H.Tr>
            </:head>
            <:body as |B|>
              {{#each assignments as |assignment|}}
                {{! @glint-expect-error }}{{! prettier-ignore }}
                {{#let (iamFindRoleFromRoles (get (or (findBy "link.type" assignment.link.type @rolesLocations) @currentRolesLocation) "roles") roleId=assignment.roleId) as |role|}}
                      <B.Tr data-test-role-assignments-list-table-row>
                        <B.Td
                          data-test-role-assignments-list-table-cell
                          data-test-role-assignments-list-table-cell-role
                          data-test-role-assignments-list-table-cell-name="role"
                        >
                          {{#if role}}
                            <RoleBadge @role={{role}} />
                          {{else}}
                            {{assignment.roleId}}
                          {{/if}}
                        </B.Td>
                        <B.Td
                          data-test-role-assignments-list-table-cell
                          data-test-role-assignments-list-table-cell-role-description
                          data-test-role-assignments-list-table-cell-name="description"
                        >
                          {{or role.description "—"}}
                        </B.Td>
                        <B.Td
                          data-test-role-assignments-list-table-cell
                          data-test-role-assignments-list-table-cell-links
                          data-test-role-assignments-list-table-cell-name="links"
                        >

                          <Flex
                            @direction={{FlexDirection.Column}}
                            @gap={{FlexGap.Xs}}
                          >
                            {{#let
                              (optionForSourceState
                                "resource-type" assignment.link.type
                              )
                              as |assignmentOption|
                            }}
                              <HdsRichTooltip as |RT|>
                                <RT.Toggle
                                  @size="medium"
                                  @text={{assignment.link.name}}
                                  {{! @glint-expect-error: fix incoming types }}
                                  @icon={{assignmentOption.icon}}
                                  @iconPosition="leading"
                                />
                                <RT.Bubble @placement="top">
                                  <HdsTextBody>
                                    {{t
                                      "manage-access.components.table.member-role-assignments.scope-name-scope-type"
                                      scopeName=assignment.link.name
                                      scopeType=(lowercase
                                        (t assignmentOption.text)
                                      )
                                    }}
                                  </HdsTextBody>
                                </RT.Bubble>
                              </HdsRichTooltip>
                            {{/let}}

                            {{#if
                              (eq
                                assignment.member.memberType
                                HashicorpCloudResourcemanagerPolicyBindingMemberType.GROUP
                              )
                            }}
                              {{#let
                                (optionForSourceState
                                  "policy-type" assignment.member.memberType
                                )
                                as |memberOption|
                              }}
                                {{#let
                                  (findBy
                                    "resourceId"
                                    assignment.member.memberId
                                    @groups
                                  )
                                  as |group|
                                }}
                                  <HdsRichTooltip as |RT|>
                                    <RT.Toggle
                                      @size="medium"
                                      @text={{group.displayName}}
                                      {{! @glint-expect-error: fix incoming types }}
                                      @icon={{memberOption.icon}}
                                      @iconPosition="leading"
                                    />
                                    <RT.Bubble @placement="left">
                                      <HdsTextBody>
                                        {{t
                                          "manage-access.components.table.member-role-assignments.group-name"
                                          groupName=group.displayName
                                          htmlSafe=true
                                        }}
                                      </HdsTextBody>
                                    </RT.Bubble>
                                  </HdsRichTooltip>
                                {{/let}}
                              {{/let}}
                            {{/if}}
                          </Flex>
                        </B.Td>
                        <B.Td
                          data-test-role-assignments-list-table-cell
                          data-test-role-assignments-list-table-cell-granted-at
                          data-test-role-assignments-list-table-cell-name="granted-at"
                        >
                          <Flex
                            @alignItems={{AlignItem.Start}}
                            @direction={{FlexDirection.Column}}
                            @gap={{FlexGap.Xs}}
                            data-test-role-assignments-list-table-cell-links
                          >
                            {{#let
                              (optionForSourceState
                                "resource-type" assignment.link.type
                              )
                              as |assignmentOption|
                            }}
                              <Flex
                                @alignItems={{AlignItem.Start}}
                                @direction={{FlexDirection.Row}}
                                @gap={{FlexGap.Xs}}
                                data-test-role-assignments-list-table-cell-granted-at-type
                                ...attributes
                              >
                                <span>
                                  {{t assignmentOption.text}}

                                  {{#if
                                    (eq
                                      assignment.member.memberType
                                      HashicorpCloudResourcemanagerPolicyBindingMemberType.GROUP
                                    )
                                  }}
                                    {{#let
                                      (optionForSourceState
                                        "policy-type"
                                        assignment.member.memberType
                                      )
                                      as |groupOption|
                                    }}
                                      &nbsp;/&nbsp;
                                      {{t groupOption.text}}
                                    {{/let}}
                                  {{/if}}
                                </span>
                              </Flex>
                            {{/let}}
                            {{#if
                              (notEq @currentScopeLink.id assignment.link.id)
                            }}
                              <HdsRichTooltip as |RT|>
                                <RT.Toggle>
                                  <HdsBadge
                                    @icon="corner-down-right"
                                    @size="small"
                                    @text={{t
                                      "manage-access.components.table.member-role-assignments.inherited"
                                    }}
                                    data-test-role-assignments-list-table-cell-granted-inheritence
                                  />
                                </RT.Toggle>
                                <RT.Bubble @placement="bottom">
                                  <HdsTextBody>
                                    {{t
                                      "manage-access.components.table.member-role-assignments.inherited-from-outside-the-current-scope"
                                      htmlSafe=true
                                    }}
                                  </HdsTextBody>
                                </RT.Bubble>
                              </HdsRichTooltip>
                            {{/if}}
                          </Flex>
                        </B.Td>
                      </B.Tr>
                    {{/let}}
              {{/each}}
            </:body>
          </HdsTable>
          <div data-test-pagination>
            {{! @glint-expect-error: fix types }}
            <HdsPaginationNumbered
              @currentPage={{@roleAssignments.currentPage}}
              @currentPageSize={{@roleAssignments.currentPageSize}}
              @onPageChange={{@handleOnPageChanged}}
              @onPageSizeChange={{@handleOnRolesPageSizeChanged}}
              @pageSizes={{this.pageSizes}}
              @totalItems={{@roleAssignments.totalItems}}
            />
          </div>
        </Flex>
      {{else}}
        <div data-test-empty-state {{boxPadding "xl 0"}}>
          {{#if @isFiltered}}
            <HdsApplicationState as |A|>
              <A.Header
                @icon="alert-circle"
                @title={{t
                  "manage-access.components.table.member-role-assignments.no-results"
                }}
              />
              <A.Body
                @text={{t
                  "manage-access.components.table.member-role-assignments.try-adjusting-your-applied-filters"
                }}
              />
              <A.Footer @hasDivider={{true}} as |F|>
                <F.Button
                  @icon="x"
                  @text={{t
                    "manage-access.components.table.member-role-assignments.clear-filters"
                  }}
                  @color="tertiary"
                  data-test-role-assignments-list-clear-filters
                  {{on "click" @handleOnClearFilters}}
                />
              </A.Footer>

            </HdsApplicationState>
          {{else}}
            <HdsApplicationState as |A|>
              <A.Header
                @title={{t
                  "manage-access.components.table.member-role-assignments.no-scope-level-roles-assigned"
                }}
              />
              <A.Body
                @text={{t
                  "manage-access.components.table.member-role-assignments.assign-roles-or-view-inherited-roles"
                }}
              />
              <A.Footer @hasDivider={{true}} as |F|>
                <F.LinkStandalone
                  @icon="plus"
                  @iconPosition="leading"
                  @route="detail.edit"
                  @text={{t
                    "manage-access.components.table.member-role-assignments.add-new-assignment"
                  }}
                  data-test-role-assignments-list-add-new-assignment
                />
              </A.Footer>
            </HdsApplicationState>
          {{/if}}
        </div>
      {{/if}}
    {{/let}}
  </template>
}
